/** Shared trigger store for the single recording-key prompt. */

import { get, writable } from 'svelte/store';

export interface CameraKeyPromptState {
	/** Normalised device id to unlock. */
	deviceId: string;
	/** Marks a wrong/rotated key so the prompt shows a wrong-password error. */
	wrongKey: boolean;
	/** Recording ISO date selecting the era to unlock (newest when empty). */
	date: string;
	/** Whether the prompt is open. */
	open: boolean;
	/** Device ids already prompted this session (auto-open skips them). */
	promptedDevices: string[];
}

const initialState: CameraKeyPromptState = {
	deviceId: '',
	wrongKey: false,
	date: '',
	open: false,
	promptedDevices: [],
};

function create_camera_key_prompt() {
	const { subscribe, set, update } = writable<CameraKeyPromptState>({ ...initialState });

	return {
		subscribe,
		/** Opens the prompt for a device's era and records it for {@link hasPrompted}. */
		open: (deviceId: string, wrongKey: boolean, date = '') =>
			update((state) => ({
				...state,
				deviceId,
				wrongKey,
				date,
				open: true,
				promptedDevices: [...new Set([...state.promptedDevices, deviceId])],
			})),
		/** Closes the prompt. */
		close: () => update((state) => ({ ...state, open: false, wrongKey: false })),
		/** Whether the device was already prompted this session. */
		hasPrompted: (deviceId: string): boolean =>
			get(camera_key_prompt).promptedDevices.includes(deviceId),
		/** Clears session state (call on logout). */
		reset: () => set({ ...initialState }),
	};
}

export const camera_key_prompt = create_camera_key_prompt();
